import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Switch,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { unwrap } from '../api/client';
import { grantLernfachTime, lernfach, setLernfachSubject } from '../api/gen';
import type { LernfachCard, LernfachOverview } from '../api/gen';
import { dateTime } from '../lib/text';
import { useOrg } from '../lib/session';

// Lernfach as a parent runs it (decision 0009): which subjects are open for
// the child, where each card stands in its five Stufen, which side of a skill
// keeps tripping the child, the last rounds, and today's time.
//
// The Stufen are evidence beside the Lehrplan's Stand, not a grade. A card in
// Stufe 1 is a skill being practised, and the page says which part of it.
export function LernfachSection({ childId }: { childId: string }) {
  const org = useOrg();
  const queryClient = useQueryClient();
  const key = ['lernfach', childId];

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => unwrap(lernfach({ path: { childId }, headers: org })),
  });

  const set = useMutation({
    mutationFn: (v: { subject: string; open: boolean }) =>
      unwrap(setLernfachSubject({ path: { childId, subject: v.subject }, headers: org, body: { open: v.open } })),
    onSuccess: (ov) => ov && queryClient.setQueryData<LernfachOverview>(key, ov),
    onError: (err) => notifications.show({ color: 'red', message: (err as Error).message }),
  });

  const grant = useMutation({
    mutationFn: () => unwrap(grantLernfachTime({ path: { childId }, headers: org, body: {} })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
      notifications.show({ message: '30 Minuten freigegeben.' });
    },
    onError: (err) => notifications.show({ color: 'red', message: (err as Error).message }),
  });

  if (isLoading || !data) return <Loader size="sm" />;
  const clock = data.clock;

  return (
    <Card withBorder padding="lg">
      <Stack gap="md">
        <div>
          <Title order={5}>Lernfach</Title>
          <Text size="sm" c="dimmed">
            Neben den Hausaufgaben: Lernkarten, die bei jedem Öffnen neue Aufgaben stellen. Eine Karte
            steigt über fünf Stufen, bis sie sitzt. Das Kind sieht nur die Fächer, die Sie öffnen.
          </Text>
        </div>

        {data.subjects.map((s) => (
          <Stack key={s.id} gap="xs">
            <Group justify="space-between">
              <Switch
                label={s.name}
                checked={s.open}
                disabled={set.isPending}
                onChange={(e) => set.mutate({ subject: s.id, open: e.currentTarget.checked })}
              />
              {s.cards.length > 0 ? (
                <Text size="xs" c="dimmed">
                  {s.learnt} von {s.total} Karten sitzen · {s.due} heute fällig
                </Text>
              ) : null}
            </Group>
            {s.cards.map((c) => (
              <CardRow key={c.skill} card={c} />
            ))}
            {s.open && s.cards.length === 0 ? (
              <Text size="sm" c="dimmed">
                Die ersten Karten kommen mit der ersten Runde.
              </Text>
            ) : null}
          </Stack>
        ))}

        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {clock.remaining > 0
              ? `Heute noch ${Math.ceil(clock.remaining / 60)} Minuten freigegeben`
              : clock.started
                ? 'Die freigegebene Zeit ist für heute aufgebraucht.'
                : 'Die erste halbe Stunde beginnt mit der ersten Runde des Tages.'}
          </Text>
          <Button variant="light" size="xs" loading={grant.isPending} onClick={() => grant.mutate()}>
            30 Minuten freigeben
          </Button>
        </Group>

        {data.recent.length > 0 ? (
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Letzte Runden
            </Text>
            {data.recent.map((r) => (
              <Group key={r.id} justify="space-between">
                <Text size="sm">
                  {r.climbed} von {r.cards} Karten gestiegen
                  {r.good ? ' · gut gelaufen' : ''}
                </Text>
                <Text size="xs" c="dimmed">
                  {dateTime(r.finishedAt)}
                </Text>
              </Group>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Card>
  );
}

function CardRow({ card }: { card: LernfachCard }) {
  const box = card.box ?? 1;
  const miss = card.misses?.[0];
  return (
    <Box pl="md">
      <Group justify="space-between" wrap="nowrap">
        <Text size="sm">{card.title}</Text>
        <Group gap={6} wrap="nowrap">
          {card.learnt ? (
            <Badge variant="light" color="green">
              sitzt
            </Badge>
          ) : null}
          <Tooltip label={`Stufe ${box} von 5`}>
            <Group gap={3} wrap="nowrap" aria-label={`Stufe ${box} von 5`}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Box
                  key={i}
                  w={10}
                  h={10}
                  style={{
                    borderRadius: 3,
                    background: i <= box ? 'var(--mantine-color-gray-8)' : 'var(--mantine-color-gray-2)',
                  }}
                />
              ))}
            </Group>
          </Tooltip>
        </Group>
      </Group>
      {miss || (card.lapses ?? 0) > 0 ? (
        <Text size="xs" c="dimmed">
          {(card.lapses ?? 0) > 0 ? `${card.lapses}× zurück auf Stufe 1` : ''}
          {miss && (card.lapses ?? 0) > 0 ? ' · ' : ''}
          {miss ? `hakt bei: ${miss.facet}` : ''}
        </Text>
      ) : null}
    </Box>
  );
}
